import { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";

export default function OrgsUsers(){
  const [orgs,setOrgs]=useState([]);
  const [sel,setSel]=useState(null);
  const [search,setSearch]=useState("");
  const [create,setCreate]=useState({ name:"", slug:"", privacy_policy_url:"", logo_url:"", theme:{ primary:'#0a2a66', primaryText:'#ffffff', secondary:'#ffffff', secondaryText:'#0a2a66', bg:'#ffffff', text:'#000000', show_logo:true } });
  const [user,setUser]=useState({ email:"", password:"" });
  const load=async()=>{ try { const r=await api.get('/owner/orgs'); setOrgs(r.data.rows||[]); } catch(e){ setOrgs([]); } };
  useEffect(()=>{ load(); },[]);

  const doCreate=async()=>{
    const body={ ...create, theme:create.theme };
    const r=await api.post('/owner/orgs', body); setSel(r.data); load();
  };
  const saveTheme=async()=>{
    if(!sel) return; await api.patch(`/owner/orgs/${sel.id}`, { theme: sel.theme, privacy_policy_url: sel.privacy_policy_url, logo_url: sel.logo_url, name: sel.name, tier: sel.tier }); toast("Saved"); load();
  };
  const orgAction = async(action)=>{
    if(!sel) return;
    if(action==='delete' && !confirm('Delete this org? Data will be retained for 90 days, then purged.')) return;
    await api.post(`/owner/orgs/${sel.id}/action`, { action });
    // refresh orgs and selected org state
    const r = await api.get('/owner/orgs');
    const list = r.data.rows || [];
    setOrgs(list);
    const updated = list.find(o=> o.id === sel.id);
    if(updated) setSel(updated);
  };
  const addUser=async()=>{
    if(!sel) return; await api.post('/owner/users', { email:user.email, org_id: sel.id, password: user.password }); setUser({ email:"", password:"" });
  };
  const listUsers=async()=>{ if(!sel) return []; const r=await api.get('/owner/users', { params: { org_id: sel.id }}); return r.data.rows||[]; };
  const [users,setUsers]=useState([]);
  useEffect(()=>{ (async()=>{ if(sel){ setUsers(await listUsers()); } })(); },[sel]);
  const impersonate=async(u)=>{ 
    try {
      const r=await api.post(`/owner/impersonate/${u.id}`); 
      const current=localStorage.getItem('token'); 
      if(current){ localStorage.setItem('owner_token_backup', current); } 
      localStorage.setItem('token', r.data.access_token);
      // Clear old org info so it gets reloaded with the new org
      localStorage.removeItem('orgInfo');
      window.location.href='/campaigns'; 
    } catch(err) {
      console.error("Impersonation error:", err);
      toast.error(err.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const [showPendingDeletion, setShowPendingDeletion] = useState(false);
  
  // Separate active orgs from those pending deletion
  const allFiltered = orgs.filter(o=> !search || (o.name||'').toLowerCase().includes(search.toLowerCase()) || (o.slug||'').toLowerCase().includes(search.toLowerCase()));
  const activeOrgs = allFiltered.filter(o => !o.deleted_at);
  const pendingDeletionOrgs = allFiltered.filter(o => o.deleted_at);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="orgs-users-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>Owner Console</h1>
        <p className="text-sm text-gray-500">Manage organisations and users.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Create Organisation</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input data-testid="org-name" placeholder="Name" value={create.name} onChange={(e)=>setCreate({...create,name:e.target.value})} />
          <Input data-testid="org-slug" placeholder="Slug (optional)" value={create.slug} onChange={(e)=>setCreate({...create,slug:e.target.value})} />
          <Input data-testid="org-privacy" placeholder="Privacy policy URL" value={create.privacy_policy_url} onChange={(e)=>setCreate({...create,privacy_policy_url:e.target.value})} />
          <Input data-testid="org-logo" placeholder="Logo URL" value={create.logo_url} onChange={(e)=>setCreate({...create,logo_url:e.target.value})} />
          <div className="col-span-full grid grid-cols-2 gap-2">
            <label className="text-sm">Primary <Input data-testid="org-theme-primary" type="color" value={create.theme.primary} onChange={(e)=>setCreate({...create, theme:{...create.theme, primary:e.target.value}})} /></label>
            <label className="text-sm">Secondary <Input data-testid="org-theme-secondary" type="color" value={create.theme.secondary} onChange={(e)=>setCreate({...create, theme:{...create.theme, secondary:e.target.value}})} /></label>
            <label className="text-sm">Background <Input data-testid="org-theme-bg" type="color" value={create.theme.bg} onChange={(e)=>setCreate({...create, theme:{...create.theme, bg:e.target.value}})} /></label>
          <div className="col-span-full">
            <label className="block text-sm mb-1">Upload Logo (PNG/JPG/WebP)</label>
            <input data-testid="org-logo-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={async(e)=>{
              const f=e.target.files?.[0]; if(!f) return;
              try{
                const form=new FormData(); form.append('file', f);
                const r=await api.post(`/owner/orgs/${sel?.id||create.id}/logo`, form, { headers:{ 'Content-Type':'multipart/form-data' }});
                const url=r.data.logo_url; if(sel){ setSel({...sel, logo_url:url}); } else { setCreate({...create, logo_url:url}); }
                toast("Logo uploaded");
              }catch(err){ toast("Upload failed"); }
            }} />
          </div>

            <label className="text-sm">Text <Input data-testid="org-theme-text" type="color" value={create.theme.text} onChange={(e)=>setCreate({...create, theme:{...create.theme, text:e.target.value}})} /></label>
            <label className="flex items-center gap-2 col-span-full text-sm"><input data-testid="org-theme-show-logo" type="checkbox" checked={!!create.theme.show_logo} onChange={(e)=>setCreate({...create, theme:{...create.theme, show_logo:e.target.checked}})} /> Show logo</label>
          </div>
          <Button data-testid="org-create" onClick={doCreate}>Create</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active Clients ({activeOrgs.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <Input data-testid="orgs-search" placeholder="Search organisations" value={search} onChange={(e)=>setSearch(e.target.value)} />
          </div>
          <div className="overflow-auto border rounded-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="text-left p-2 w-32">Manage</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Slug</th>
                  <th className="text-left p-2">Tier</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Privacy URL</th>
                </tr>
              </thead>
              <tbody>
                {activeOrgs.length === 0 ? (
                  <tr><td colSpan={6} className="p-4 text-center text-gray-500">No active organisations found</td></tr>
                ) : (
                  activeOrgs.map(o=> (
                    <tr key={o.id} className="border-t">
                      <td className="p-2"><Button size="sm" variant="outline" data-testid={`org-manage-${o.slug}`} onClick={()=>setSel(o)}>Manage</Button></td>
                      <td className="p-2">{o.name}</td>
                      <td className="p-2">{o.slug}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${o.tier === 1 ? 'bg-gray-100' : o.tier === 2 ? 'bg-blue-100' : 'bg-green-100'}`}>
                          Tier {o.tier || 3}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${o.enabled === false ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {o.enabled === false ? 'Paused' : 'Active'}
                        </span>
                      </td>
                      <td className="p-2 truncate max-w-xs">{o.privacy_policy_url && /^https?:\/\//i.test(o.privacy_policy_url) ? <a data-testid={`org-privacy-link-${o.id}`} className="underline" href={o.privacy_policy_url} target="_blank" rel="noopener noreferrer">{o.privacy_policy_url}</a> : <span data-testid={`org-privacy-missing-${o.id}`}>—</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pending Deletion Section - Expandable */}
          {pendingDeletionOrgs.length > 0 && (
            <div className="mt-4">
              <button 
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                onClick={() => setShowPendingDeletion(!showPendingDeletion)}
                data-testid="toggle-pending-deletion"
              >
                <span className={`transform transition-transform ${showPendingDeletion ? 'rotate-90' : ''}`}>▶</span>
                <span className="text-red-600">Pending Deletion ({pendingDeletionOrgs.length})</span>
              </button>
              
              {showPendingDeletion && (
                <div className="mt-2 overflow-auto border border-red-200 rounded-md bg-red-50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-red-100">
                        <th className="text-left p-2 w-32">Manage</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Slug</th>
                        <th className="text-left p-2">Deletion Scheduled</th>
                        <th className="text-left p-2">Days Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDeletionOrgs.map(o=> {
                        const deletedAt = new Date(o.deleted_at);
                        const purgeDate = new Date(deletedAt.getTime() + 90 * 24 * 60 * 60 * 1000);
                        const daysRemaining = Math.max(0, Math.ceil((purgeDate - new Date()) / (1000 * 60 * 60 * 24)));
                        return (
                          <tr key={o.id} className="border-t border-red-200">
                            <td className="p-2"><Button size="sm" variant="outline" data-testid={`org-manage-${o.slug}`} onClick={()=>setSel(o)}>Manage</Button></td>
                            <td className="p-2">{o.name}</td>
                            <td className="p-2">{o.slug}</td>
                            <td className="p-2 text-red-700">{deletedAt.toLocaleDateString()}</td>
                            <td className="p-2">
                              <span className="px-2 py-0.5 rounded text-xs bg-red-200 text-red-800">
                                {daysRemaining} days until purge
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {sel && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid="org-editor">
              <Input data-testid="sel-name" value={sel.name} onChange={(e)=>setSel({...sel,name:e.target.value})} />
              <Input data-testid="sel-privacy" value={sel.privacy_policy_url||""} onChange={(e)=>setSel({...sel,privacy_policy_url:e.target.value})} />
              <Input data-testid="sel-logo" value={sel.logo_url||""} onChange={(e)=>setSel({...sel,logo_url:e.target.value})} />
              <div className="col-span-full">
                <label className="block text-sm mb-1">Upload Logo (PNG/JPG/WebP)</label>
                <input data-testid="sel-logo-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={async(e)=>{
                  const f=e.target.files?.[0]; if(!f||!sel?.id) return;
                  try{
                    const form=new FormData(); form.append('file', f);
                    const r=await api.post(`/owner/orgs/${sel.id}/logo`, form, { headers:{ 'Content-Type':'multipart/form-data' }});
                    setSel({...sel, logo_url:r.data.logo_url});
                    toast("Logo uploaded");
                  }catch(err){ toast("Upload failed"); }
                }} />
              </div>
              <label className="text-sm">Primary <Input type="color" value={sel.theme?.primary||'#0a2a66'} onChange={(e)=>setSel({...sel, theme:{...sel.theme, primary:e.target.value}})} /></label>
              
              {/* Tier Selection */}
              <div className="col-span-full">
                <label className="text-sm font-medium block mb-1">Campaign Tier</label>
                <select 
                  className="border rounded px-3 py-2 w-full max-w-xs"
                  value={sel.tier || 3}
                  onChange={(e) => setSel({...sel, tier: parseInt(e.target.value)})}
                >
                  <option value={1}>Tier 1 - 1 active campaign</option>
                  <option value={2}>Tier 2 - Up to 25 campaigns</option>
                  <option value={3}>Tier 3 - Unlimited campaigns</option>
                </select>
              </div>
              
              <div className="col-span-full flex gap-2 items-center">
                {sel.enabled!==false ? (
                  <Button size="sm" variant="outline" onClick={()=>orgAction('pause')}>Pause</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={()=>orgAction('resume')}>Resume</Button>
                )}
                <Button size="sm" variant="destructive" onClick={()=>orgAction('delete')}>Delete</Button>
                <span className="text-xs text-neutral-600">Status: {sel.deleted_at? 'Deletion scheduled' : (sel.enabled===false? 'Paused' : 'Active')}</span>
              </div>

              <label className="text-sm">Secondary <Input type="color" value={sel.theme?.secondary||'#ffffff'} onChange={(e)=>setSel({...sel, theme:{...sel.theme, secondary:e.target.value}})} /></label>
              <label className="text-sm">Background <Input type="color" value={sel.theme?.bg||'#ffffff'} onChange={(e)=>setSel({...sel, theme:{...sel.theme, bg:e.target.value}})} /></label>
              <label className="text-sm">Text <Input type="color" value={sel.theme?.text||'#000000'} onChange={(e)=>setSel({...sel, theme:{...sel.theme, text:e.target.value}})} /></label>
              <label className="flex items-center gap-2 col-span-full text-sm"><input type="checkbox" checked={sel.theme?.show_logo!==false} onChange={(e)=>setSel({...sel, theme:{...sel.theme, show_logo:e.target.checked}})} /> Show logo</label>
              <div className="col-span-full flex gap-2"><Button data-testid="sel-save" onClick={saveTheme}>Save</Button></div>
            </div>
          )}
        </CardContent>
      </Card>

      {sel && (
        <Card>
          <CardHeader><CardTitle>Users for {sel.name}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 items-end mb-3">
              <Input data-testid="user-email" placeholder="user email" value={user.email} onChange={(e)=>setUser({...user,email:e.target.value})} />
              <Input data-testid="user-pass" placeholder="temp password" value={user.password} onChange={(e)=>setUser({...user,password:e.target.value})} />
              <Button data-testid="user-add" onClick={addUser}>Add User</Button>
            </div>
            <table className="w-full text-sm">
              <thead><tr><th>Email</th><th>Actions</th></tr></thead>
      <Toaster position="top-right" richColors />

              <tbody>
                {users.map(u=> (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td className="space-x-2">
                      <Button size="sm" variant="outline" data-testid={`impersonate-${u.id}`} onClick={()=>impersonate(u)}>Impersonate</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
